import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { toSentenceCase } from 'common/utils/utilFunctions';

import {
	ICitationDetailsElements,
	ICitationsUpdateRequestParams,
	IGroupAssessmentChangeParams,
	IGroupAssessmentUpdateRequest,
	ISelectedCitationGroups,
} from '../api/type-def';
import { ICustomBreadcrumbsProps } from '../Components/RegulationBreadcrumb/RegulationBreadcrumbs';

const { regulationInventory, overView } = rrcpDashboardRoutes;
/**
 * Check if all children selected
 * @param selectedCitationArray Selected checkbox list
 * @param citationElement Parent element
 * @returns True or false
 */
export const checkAnyChildrenSelected = (
	selectedCitationArray: ISelectedCitationGroups[],
	citationElement: ICitationDetailsElements,
) => {
	if (selectedCitationArray && citationElement?.children.length) {
		const isAllChildrenSelected = citationElement?.children.some(
			(subOption) => {
				const childSelected = selectedCitationArray.some(
					(ele) => ele.citationId === subOption?.id,
				);
				if (!childSelected) {
					return checkAnyChildrenSelected(selectedCitationArray, subOption);
				}
				return childSelected;
			},
		);
		return isAllChildrenSelected;
	}
	return false;
};

/**
 * Check if all children selected
 * @param selectedCitationArray Selected checkbox list
 * @param citationElement Parent element
 * @returns True or false
 */
export const checkAllChildrenSelected = (
	selectedCitationArray: ISelectedCitationGroups[],
	citationElement: ICitationDetailsElements,
) => {
	if (selectedCitationArray && citationElement?.children.length) {
		const isAllChildrenSelected = citationElement?.children.every((subOption) =>
			selectedCitationArray.some((ele) => ele.citationId === subOption?.id),
		);
		return isAllChildrenSelected;
	}
	return false;
};

/**
 * This Method will Update selectedCitationArray, will add parents if all children selected
 * @param selectedCitationArray Selected array,
 * @param parents Root node
 */
export const getAllSelectedParents = (
	selectedCitationArray: ISelectedCitationGroups[],
	parents: ICitationDetailsElements,
	parentId: string,
) => {
	if (parents?.children.length) {
		parents.children.forEach((node) => {
			getAllSelectedParents(selectedCitationArray, node, parentId);
		});
	}
	if (checkAllChildrenSelected(selectedCitationArray, parents)) {
		selectedCitationArray.push({
			citationId: parents.id,
			parentId,
			group: [],
		});
	}
};

/**
 * This Method will Update selectedCitationArray, will remove parents if any children un-selected
 * @param selectedCitationArray Selected array,
 * @param parents Root node
 */
export const getAllUnSelectedParents = (
	selectedCitationArray: ISelectedCitationGroups[],
	parents: ICitationDetailsElements,
) => {
	if (parents?.children.length) {
		parents.children.forEach((node) => {
			getAllUnSelectedParents(selectedCitationArray, node);
		});
	}
	const isAllChildrenSelected = checkAllChildrenSelected(
		selectedCitationArray,
		parents,
	);
	const index = selectedCitationArray.findIndex(
		(elem) => elem.citationId === parents.id,
	);
	if (index !== -1 && !isAllChildrenSelected && parents?.children.length) {
		selectedCitationArray.splice(index, 1);
	}
};

export const getAllChildrenNodes = (citationsObj: ICitationDetailsElements) => {
	const result: string[] = [];
	if (citationsObj) {
		result.push(citationsObj.id);
		if (citationsObj?.children) {
			let childKeys: string[] = [];
			citationsObj.children.forEach((childObj) => {
				childKeys = getAllChildrenNodes(childObj);
				result.push(...childKeys);
			});
		}
	}
	return result;
};

export const getGroupsPerRegulation = (
	root: ICitationDetailsElements,
	parentId: string,
	result: ISelectedCitationGroups[],
) => {
	if (root?.children?.length) {
		root.children.forEach((node) => {
			getGroupsPerRegulation(node, parentId, result);
		});
	}
	if (root && root?.group?.length) {
		const citationGroup = {
			citationId: root.id,
			group: [...root.group],
			parentId,
		} as ISelectedCitationGroups;
		result.push(citationGroup);
	}
};

export const getGroupingBreadCrumbs = (
	breadCrumbText: string,
	regInvSelectedTab: string,
	title?: string,
) => {
	const breadCrumbs: ICustomBreadcrumbsProps[] = [
		{
			title: toSentenceCase(breadCrumbText),
			type: 'link',
			key: 'req-inv',
			path: regulationInventory,
			dataTestId: 'inventory-breadcrumbs-link',
		},
		{
			title: toSentenceCase(regInvSelectedTab),
			type: 'link',
			key: 'req-inv-selected-tab',
			path: `${regulationInventory}/${overView}`,
			dataTestId: 'inventory-breadcrumbs-link-selected-tab',
		},
		{
			title: title || '',
			type: 'text',
			key: 'static-text',
		},
	];
	return breadCrumbs;
};

export const getGroupAssessmentAPIParams = (
	regulationGroups: ISelectedCitationGroups[],
	taskDetails: ICitationsUpdateRequestParams,
	originalRegulationGroups: ISelectedCitationGroups[],
	status?: string,
) => {
	const postGroups: IGroupAssessmentChangeParams[] = [];
	const putGroups: IGroupAssessmentChangeParams[] = [];

	regulationGroups.forEach((elem) => {
		const isPresent = originalRegulationGroups.find(
			(existingObj) => existingObj.citationId === elem.citationId,
		);
		const obj: IGroupAssessmentChangeParams = {
			citationId: elem.citationId,
			regulationRelationshipId: elem.parentId || '',
			groups: elem.group.map((e) => e.groupId),
		};
		if (isPresent) {
			putGroups.push(obj);
		} else if (obj.groups.length) {
			postGroups.push(obj);
		}
	});

	const postArgument: IGroupAssessmentUpdateRequest = {
		taskDetails: {
			...taskDetails,
			assessmentUpdate: false,
			taskStatus: status || taskDetails.taskStatus,
		},
		grouping: postGroups.filter(
			(item, index, self) =>
				index === self.findIndex((temp) => temp.citationId === item.citationId),
		),
	};
	const putArgument: IGroupAssessmentUpdateRequest = {
		taskDetails: {
			...taskDetails,
			assessmentUpdate: true,
			taskStatus: status || taskDetails.taskStatus,
		},
		grouping: putGroups.filter(
			(item, index, self) =>
				index === self.findIndex((temp) => temp.citationId === item.citationId),
		),
	};
	return { postArgument, putArgument };
};

/// This method is used to select all regulations/accordions in group assessment.
export const checkAllCitationForRegulation = (
	checkedCitations: ISelectedCitationGroups[],
	node: ICitationDetailsElements,
	rootNode?: ICitationDetailsElements | null,
) => {
	const nodes = getAllChildrenNodes(node);
	const selectedNodes: ISelectedCitationGroups[] = [];
	nodes.forEach((childNode: string) => {
		const checkedObj: ISelectedCitationGroups = {
			citationId: childNode,
			parentId: rootNode?.id || node.id,
			group: [],
		};
		selectedNodes.push(checkedObj);
	});
	const combinedArray = [...checkedCitations, ...selectedNodes];
	const newCitationArray: ISelectedCitationGroups[] = combinedArray;
	// Include parent if all children selected
	if (rootNode) {
		getAllSelectedParents(newCitationArray, rootNode, rootNode.id);
	}
	// Remove duplicate is any
	const uniqArray = newCitationArray.filter(
		(item, index, self) =>
			index ===
			self.findIndex(
				(temp: ISelectedCitationGroups) => temp.citationId === item.citationId,
			),
	);
	return [...uniqArray];
};

/// This method is used to un-select all regulations/accordions in group assessment.
export const unCheckAllCitationForRegulation = (
	checkedCitations: ISelectedCitationGroups[],
	node: ICitationDetailsElements,
	rootNode?: ICitationDetailsElements | null,
) => {
	const nodes = getAllChildrenNodes(node);
	let removedArray = checkedCitations;
	nodes.forEach((childNode: string) => {
		removedArray = removedArray.filter((elem) => elem.citationId !== childNode);
	});
	const newCitationArray: ISelectedCitationGroups[] = removedArray;
	// un-Select all parent if any children unselected
	if (rootNode) {
		getAllUnSelectedParents(newCitationArray, rootNode);
	}
	return newCitationArray;
};

export const getSelectedChildrenDescription = (
	nodes: ICitationDetailsElements,
	selectedCitationArray: ISelectedCitationGroups[],
	result: string[],
) => {
	if (nodes) {
		const selectedNode = selectedCitationArray.some(
			(item) => item.citationId === nodes.id,
		);
		if (selectedNode && nodes.summary && nodes.regulation) {
			result.push(nodes.summary ? nodes.summary.toString() : '');
		}
		if (selectedNode && nodes.description && !nodes.regulation) {
			result.push(nodes.description);
		}
		if (nodes.children) {
			nodes.children.forEach((childObj) => {
				getSelectedChildrenDescription(childObj, selectedCitationArray, result);
			});
		}
	}
};

export const getAllSelectedCitationsText = (
	grouping: ICitationDetailsElements[],
	selectedCitationArray: ISelectedCitationGroups[],
) => {
	const result: string[] = [];
	grouping.forEach((node) => {
		getSelectedChildrenDescription(node, selectedCitationArray, result);
	});
	return result;
};
